import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { CompanyStateResponse, getCompanyState } from '../../api/documentIntelligence';

interface CompanyChoice {
  id: string;
  name: string;
}

interface Props {
  company: CompanyChoice | null;
  environment: string;
  refreshToken?: string | null;
}

const titleCase = (value: string): string =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const CompanyStatePanel: React.FC<Props> = ({ company, environment, refreshToken }) => {
  const generation = useRef(0);
  const [state, setState] = useState<CompanyStateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const requestGeneration = ++generation.current;
    setState(null);
    setError(null);
    if (!company) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void getCompanyState(company.id)
      .then((response) => {
        if (requestGeneration === generation.current) setState(response);
      })
      .catch(() => {
        if (requestGeneration === generation.current) {
          setError('Could not load the company intelligence state.');
        }
      })
      .finally(() => {
        if (requestGeneration === generation.current) setLoading(false);
      });
  }, [company, environment, refreshToken]);

  if (!company) {
    return (
      <Alert severity="info">
        Select a company above to see its complete Milestones, Pipeline Mapping, and Corporate
        Transactions state.
      </Alert>
    );
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!state) return null;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          {state.company_name || company.name}
        </Typography>
        <Typography color="text.secondary">Current stored company intelligence</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography component="h2" variant="h6" fontWeight={700} gutterBottom>
          Milestones
        </Typography>
        {state.milestones.length === 0 ? (
          <Typography color="text.secondary">No recorded milestones.</Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {state.milestones.map((item, index) => (
              <Box key={item.id || `${item.milestone}-${index}`} sx={{ py: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography fontWeight={600}>{item.milestone}</Typography>
                  <Chip size="small" label={item.status} variant="outlined" />
                </Stack>
                {item.date_label && (
                  <Typography variant="body2" color="text.secondary">
                    {item.date_label}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography component="h2" variant="h6" fontWeight={700} gutterBottom>
          Pipeline Mapping
        </Typography>
        {state.pipeline.length === 0 ? (
          <Typography color="text.secondary">No recorded pipeline mappings.</Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {state.pipeline.map((item) => (
              <Box key={item.key} sx={{ py: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography fontWeight={600}>{item.therapeutic_name}</Typography>
                  <Chip size="small" label={titleCase(item.difference_type)} />
                  <Chip size="small" label={titleCase(item.status)} variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {[item.reported_stage, item.indication_text].filter(Boolean).join(' · ') ||
                    'No stage or indication recorded'}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography component="h2" variant="h6" fontWeight={700} gutterBottom>
          Corporate Transactions
        </Typography>
        {state.corporate_transactions.length === 0 ? (
          <Typography color="text.secondary">No recorded corporate transactions.</Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {state.corporate_transactions.map((item) => (
              <Box key={item.id} sx={{ py: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography fontWeight={600}>{titleCase(item.transaction_type)}</Typography>
                  <Chip size="small" label={titleCase(item.transaction_class)} />
                  {item.status && (
                    <Chip size="small" label={titleCase(item.status)} variant="outlined" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {item.announced_date || 'Announcement date unavailable'}
                </Typography>
                {item.summary && <Typography variant="body2">{item.summary}</Typography>}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};
